/**
 * The plan made at setup (Settings, General): the goal, the target in
 * tasks, the languages and the planned date. The data card opens with
 * it and the Analytics page's Delivery section forecasts against it.
 * Saved on its own, outside the page's main form, because the plan is
 * one JSON field rather than a set of project columns.
 */
import { useContext, useEffect, useState } from "react";
import { Button, SettingsCard, useToast } from "@humansignal/ui";
import { useAPI } from "@humansignal/core";
import { ProjectContext } from "../../providers/ProjectProvider";
import { Input } from "../../components/Form/Elements";

const field = {
  height: 40,
  boxSizing: "border-box",
  padding: "0 12px",
  border: "1px solid var(--color-neutral-border)",
  borderRadius: 5,
  background: "var(--color-neutral-background)",
  color: "var(--color-neutral-content)",
  fontSize: "var(--font-size-body-small)",
  width: "100%",
};

const label = { fontSize: "var(--font-size-body-small)", fontWeight: 500 };

export const PlanCard = () => {
  const api = useAPI();
  const toast = useToast();
  const { project, fetchProject } = useContext(ProjectContext);
  const [plan, setPlan] = useState({ goal: "", target_tasks: "", languages: "", deadline: "" });
  const [busy, setBusy] = useState(false);
  // The project arrives after the first render: fill the fields then.
  useEffect(() => {
    const saved = project?.plan || {};
    setPlan({
      goal: saved.goal || "",
      target_tasks: saved.target_tasks || "",
      languages: (saved.languages || []).join(", "),
      deadline: saved.deadline || "",
    });
  }, [project?.id, project?.plan]);

  const save = async () => {
    setBusy(true);
    const res = await api.callApi("updateProject", { params: { pk: project.id }, body: { plan }, suppressError: true });
    setBusy(false);
    if (res?.error) {
      toast.show({
        message: res.response?.plan?.[0] || res.response?.detail || "Could not save the plan.",
        type: "error",
      });
      return;
    }
    toast.show({ message: "Plan saved." });
    fetchProject?.();
  };

  if (!project?.id) return null;

  return (
    <SettingsCard
      title="Plan"
      description="What the data is for, how many tasks it should reach, in which languages, by when. The data card opens with it; Analytics, Delivery forecasts against the target."
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Input
          label="Goal"
          value={plan.goal}
          onChange={(e) => setPlan({ ...plan, goal: e.target.value })}
          placeholder="e.g. A sentiment corpus of Hausa news headlines for a classifier"
        />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label htmlFor="plan_target" style={label}>
              Target tasks
            </label>
            <input
              id="plan_target"
              type="number"
              min="0"
              value={plan.target_tasks}
              onChange={(e) => setPlan({ ...plan, target_tasks: e.target.value })}
              placeholder="e.g. 5000"
              style={field}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label htmlFor="plan_languages" style={label}>
              Languages
            </label>
            <input
              id="plan_languages"
              value={plan.languages}
              onChange={(e) => setPlan({ ...plan, languages: e.target.value })}
              placeholder="Hausa, English"
              style={field}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label htmlFor="plan_deadline" style={label}>
              Planned by
            </label>
            <input
              id="plan_deadline"
              type="date"
              value={plan.deadline}
              onChange={(e) => setPlan({ ...plan, deadline: e.target.value })}
              style={field}
            />
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button type="button" size="small" waiting={busy} onClick={save} style={{ width: 120 }}>
            Save
          </Button>
        </div>
      </div>
    </SettingsCard>
  );
};
